<template>
  <div class="content-wrapper">
    <div class="panel">
      <p class="panel-heading">{{ $t('app-dashboard') }}</p>

      <section class="panel-block">
        <b-table
          ref="table"
          :current.sync="currentPage"
          :data="taskRuns"
          :paginated="true"
          :per-page="perPage"
          pagination-size="is-small"
          detailed
          @details-open="onDetailsOpen"
        >
          <template #default="{ row: run }">
            <b-table-column :label="$t('app-name')">
              <router-link :to="`/apps/${run.task.namespace}/${run.task.version}`">
                {{ pipelineDisplayName(run.task) }} ({{ run.task.version }})
              </router-link>
            </b-table-column>

            <b-table-column :label="$t('image')">
              <router-link v-if="run.image" :to="`/project/${run.project}/image/${run.image}`">
                {{ imageNames[run.image] || run.image }}
              </router-link>
              <span v-else>—</span>
            </b-table-column>

            <b-table-column :label="$t('launched-by')" field="user.username" sortable>
              {{ run.user.username }}
            </b-table-column>

            <b-table-column :label="$t('execution-date')" field="createdAt" sortable>
              {{ formatDate(run.createdAt) }}
            </b-table-column>

            <b-table-column :label="$t('status')" field="state" centered sortable>
              <span class="tag" :class="stateClass(run.state)">
                {{ run.state }}
              </span>
            </b-table-column>

            <b-table-column :label="$t('actions')" centered>
              <div class="buttons is-centered">
                <button class="button is-small is-info is-light" @click="handleViewLogs(run)">
                  {{ $t('view-logs') }}
                </button>
                <button v-if="canManageProject" class="button is-small is-danger is-light" @click="handleDelete(run)">
                  <b-icon icon="trash" class="has-text-white" />
                </button>
              </div>
            </b-table-column>
          </template>

          <template #detail="{ row: run }">
            <b-tabs v-model="run._activeTab" type="is-boxed" size="is-small">

              <b-tab-item :label="`${$t('app-engine.inputs.title')} / ${$t('app-engine.outputs.title')}`">
                <div class="columns">
                  <div class="column">
                    <h2>{{ $t('app-engine.inputs.title') }}</h2>
                    <b-loading :is-full-page="false" :active="run.inputs === null" />
                    <task-run-parameters-table
                      v-if="run.inputs !== null"
                      :parameters="run.inputs"
                      :project-id="run.project"
                      type="input"
                    />
                  </div>
                  <div class="column">
                    <h2>{{ $t('app-engine.outputs.title') }}</h2>
                    <b-loading :is-full-page="false" :active="run.outputs === null" />
                    <task-run-parameters-table
                      v-if="run.outputs !== null"
                      :parameters="run.outputs"
                      :project-id="run.project"
                      type="outputs"
                    />
                  </div>
                </div>
              </b-tab-item>

              <b-tab-item :label="$t('logs')">
                <b-loading :is-full-page="false" :active="run.logs === null" />
                <pre v-if="run.logs !== null" class="logs">{{ run.logs }}</pre>
                <p v-else>{{ $t('no-log-to-display') }}</p>
              </b-tab-item>
            </b-tabs>
          </template>
        </b-table>
      </section>
    </div>
  </div>
</template>

<script>
import {ImageInstanceCollection} from '@/api';
import Task from '@/utils/appengine/task';
import TaskRun from '@/utils/appengine/task-run';
import TaskRunParametersTable from '@/components/appengine/task-run/TaskRunParametersTable';
import {get} from '@/utils/store-helpers';
import pipelineService from '@/api/pipeline-service';
import {pipelineDisplayName, isAnnotationImportPipeline} from '@/utils/appengine/pipeline-display';

export default {
  name: 'AppDashboardPage',
  components: {
    TaskRunParametersTable,
  },
  data() {
    return {
      taskRuns: [],
      imageNames: {},
      currentPage: 1,
      perPage: 10,
    };
  },
  computed: {
    currentProject: get('currentProject/project'),
    currentUser: get('currentUser/user'),
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
  },
  methods: {
    pipelineDisplayName,
    async fetchImageNames() {
      try {
        let images = (await ImageInstanceCollection.fetchAll({
          filterKey: 'project',
          filterValue: this.currentProject.id
        })).array;
        this.imageNames = images.reduce((acc, img) => {
          acc[img.id] = img.instanceFilename;
          return acc;
        }, {});
      } catch (e) {
        console.log(e);
      }
    },
    async fetchTaskRuns() {
      let taskRuns = await TaskRun.fetchByProject(this.currentProject.id);
      taskRuns.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      this.taskRuns = await Promise.all(
        taskRuns.map(async ({project, taskRunId, user, createdAt, image}) => {
          try {
            let taskRun = await Task.fetchTaskRunStatus(this.currentProject.id, taskRunId);
            let tr = new TaskRun({...taskRun, project, user});
            tr.image = image;
            return tr;
          } catch (e) {
            // The run's status can't be fetched (e.g. its task was deleted). Render a
            // minimal "unavailable" row — keeps the page working AND lets an admin delete
            // the orphaned run via the existing trash action (core's authenticated DELETE).
            console.log(`Task-run ${taskRunId} unavailable: ${e.message}`);
            let tr = new TaskRun({
              id: taskRunId,
              project,
              user: user || {username: '—'},
              createdAt,
              state: 'UNAVAILABLE',
              task: {name: this.$t('app-engine.task-run.unavailable'), namespace: '', version: ''},
            });
            tr.image = image;
            return tr;
          }
        })
      );
    },
    async onDetailsOpen(taskRun) {
      this.$set(taskRun, '_activeTab', taskRun._activeTab ?? 0);

      if (!taskRun.inputs) {
        await taskRun.fetchInputs();
      }

      if (!taskRun.outputs) {
        await taskRun.fetchOutputs();
      }

      if (!taskRun.logs) {
        await taskRun.fetchLogs();
      }
    },
    formatDate(date) {
      return new Intl.DateTimeFormat(
        this.$i18n.locale,
        {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }
      ).format(new Date(date));
    },
    stateClass(state) {
      const map = {
        created: 'is-light',
        provisioned: 'is-info is-light',
        queuing: 'is-warning is-light',
        queued: 'is-warning',
        running: 'is-primary',
        pending: 'is-warning is-light',
        failed: 'is-danger',
        finished: 'is-success',
      };
      return map[state.toLowerCase()] ?? 'is-light';
    },
    async handleViewLogs(run) {
      this.$set(run, '_activeTab', 1);
      await this.onDetailsOpen(run);
      this.$refs.table.openDetailRow(run);
    },
    handleDelete(run) {
      let isPipeline = isAnnotationImportPipeline(run.task);
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: isPipeline ? this.$t('confirm-deletion-run-annotations') : this.$t('confirm-deletion-run'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            // Our annotation-import pipelines: ims-service deletes the annotations + the
            // App Engine run + its core record in one call. Other app-engine runs use core's
            // own task-run DELETE.
            if (isPipeline) {
              await pipelineService.deleteRun(run.id);
            } else {
              await run.delete();
            }
            this.taskRuns = this.taskRuns.filter(tr => tr.id !== run.id);
          } catch (error) {
            console.log(error);
            this.$notify({type: 'error', text: this.$t('error-delete-run')});
          }
        },
      });
    },
  },
  async created() {
    await this.fetchImageNames();
    await this.fetchTaskRuns();
  },
};
</script>

<style>
.logs {
  font-size: 0.75rem;
  padding: 1rem;
  border-radius: 4px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
